import { lazy, Suspense, useEffect, type ComponentType } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts'
import { RequireAuth } from './components/auth/RequireAuth'
import { LoginGate } from './components/auth/LoginGate'
import { Landing } from './pages/Landing'
import { NotFound } from './pages/NotFound'

// Each page is its own chunk, so the first load only downloads the page being
// opened instead of all of them.
const pageLoaders: Record<string, () => Promise<unknown>> = {
  Import: () => import('./pages/Import'),
  RaidEvent: () => import('./pages/RaidEvent'),
  Profile: () => import('./pages/Profile'),
  PlayerProfile: () => import('./pages/PlayerProfile'),
  Raids: () => import('./pages/Raids'),
  DungeonRuns: () => import('./pages/DungeonRuns'),
  Dkp: () => import('./pages/Dkp'),
  DungeonRun: () => import('./pages/DungeonRun'),
  Roster: () => import('./pages/Roster'),
  Items: () => import('./pages/Items'),
  Bosses: () => import('./pages/Bosses'),
  BossDetail: () => import('./pages/BossDetail'),
  Settings: () => import('./pages/Settings'),
  Workshop: () => import('./pages/Workshop'),
  Crafting: () => import('./pages/Crafting'),
  CraftingProfession: () => import('./pages/CraftingProfession'),
  GuildYearbook: () => import('./pages/GuildYearbook'),
  Gallery: () => import('./pages/Gallery'),
  Archive: () => import('./pages/Archive'),
  GuildInfo: () => import('./pages/GuildInfo'),
}

// Pages use named exports, so pick the export matching the page name
const page = (name: string) =>
  lazy(() => pageLoaders[name]().then(m => ({ default: (m as Record<string, ComponentType>)[name] })))

const Import = page('Import')
const RaidEvent = page('RaidEvent')
const Profile = page('Profile')
const PlayerProfile = page('PlayerProfile')
const Raids = page('Raids')
const DungeonRuns = page('DungeonRuns')
const Dkp = page('Dkp')
const DungeonRun = page('DungeonRun')
const Roster = page('Roster')
const Items = page('Items')
const Bosses = page('Bosses')
const BossDetail = page('BossDetail')
const Settings = page('Settings')
const Workshop = page('Workshop')
const Crafting = page('Crafting')
const CraftingProfession = page('CraftingProfession')
const GuildYearbook = page('GuildYearbook')
const Gallery = page('Gallery')
const Archive = page('Archive')
const GuildInfo = page('GuildInfo')

// Same look as Layout's own loading state, since each page renders its Layout
function PageLoading() {
  return (
    <div className="min-h-screen bg-raven-darker flex items-center justify-center">
      <div className="text-gray-400">Loading...</div>
    </div>
  )
}

/**
 * The site root serves two audiences. A member gets their profile, which is
 * what "/" has always been; a stranger gets the landing page.
 */
function RootRoute() {
  const { user, loading } = useAuth()
  if (loading) return <PageLoading />
  if (user) return <Profile />
  return <Landing />
}

/** A direct link to sign in. Members who follow it are already done. */
function LoginRoute() {
  const { user, loading } = useAuth()
  if (loading) return <PageLoading />
  if (user) return <Navigate to="/" replace />
  return <LoginGate />
}

function App() {
  const { user } = useAuth()

  // Once the first page is up, fetch the other pages in the background so
  // navigating afterwards doesn't wait on a download. Members only: a
  // stranger on the landing page has no use for 26 member-page chunks.
  useEffect(() => {
    if (!user) return
    const prefetch = () => Object.values(pageLoaders).forEach(load => { load().catch(() => {}) })
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(prefetch, { timeout: 5000 })
      return () => window.cancelIdleCallback(id)
    }
    const id = setTimeout(prefetch, 2000)
    return () => clearTimeout(id)
  }, [user])

  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* Public: the landing page for strangers, the profile for members */}
        <Route path="/" element={<RootRoute />} />
        <Route path="/login" element={<LoginRoute />} />
        {/* The landing page for anyone, so officers can see and edit it */}
        <Route path="/welcome" element={<Landing />} />

        {/*
          Everything below needs an account. Until this guard existed, each
          page was protected only because it rendered a Layout that refused to
          draw when signed out - which is also why no page could be public.
        */}
        <Route element={<RequireAuth />}>
        <Route path="/profile" element={<Navigate to="/" replace />} />

        {/* Raids page */}
        <Route path="/raids" element={<Raids />} />
        <Route path="/dungeons" element={<DungeonRuns />} />
        <Route path="/dkp" element={<Dkp />} />
        <Route path="/dungeons/:runId" element={<DungeonRun />} />

        {/* Roster page (combined Players + Statistics) */}
        <Route path="/roster" element={<Roster />} />
        <Route path="/player/:characterName" element={<PlayerProfile />} />
        <Route path="/players" element={<Navigate to="/roster" replace />} />
        <Route path="/statistics" element={<Navigate to="/roster" replace />} />

        {/* Other routes */}
        <Route path="/import" element={<Import />} />
        <Route path="/event/:id" element={<RaidEvent />} />
        <Route path="/items" element={<Items />} />
        <Route path="/bosses" element={<Bosses />} />
        <Route path="/bosses/:id" element={<BossDetail />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/workshop" element={<Workshop />} />
        {/* The Apothecary became the guild Workshop */}
        <Route path="/apothecary" element={<Navigate to="/workshop" replace />} />
        <Route path="/crafting" element={<Crafting />} />
        <Route path="/crafting/:profession" element={<CraftingProfession />} />
        <Route path="/yearbook" element={<GuildYearbook />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/archive" element={<Archive />} />

        {/* Rules, loot rules and DKP, replacing Getting Started, Raven DKP+
            and Addons, which each held a piece of it */}
        <Route path="/info" element={<GuildInfo />} />
        <Route path="/getting-started" element={<Navigate to="/info" replace />} />
        <Route path="/raven-dkp-plus" element={<Navigate to="/info" replace />} />
        <Route path="/addons" element={<Navigate to="/info#raven-addons" replace />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  )
}

export default App
